import { UpdateTranslationFn } from 'articleEditor/hooks/useTranslationUpdate';
import debounce from 'just-debounce-it';
import { MyValue } from 'lib-knowledge-document-structure/plateTypes';
import {
  TranslatingStatus,
  TranslatingType,
} from 'lib-knowledge-remote-types/general.types';
import { TriggerEvent } from 'lib-knowledge-utils/analytics';
import { wait } from 'lib-knowledge-utils/promise';
import { DeepReadonly } from 'ts-essentials';
import { proxy, snapshot } from 'valtio';

import { online, trackChanges } from './savingContext.utils';

/**
 * How long to wait until we do a save. Note the initial save is always sent
 * immediately
 */
const DEBOUNCE_SAVE_DELAY = 1000;

/** Give the spinner a chance to show */
const SHOW_SAVING_FOR_MINIMUM = 800;

export type SavingState = 'saving' | 'saved' | 'error' | null;

export type ArticleTranslationDetails = {
  title: string;
  body: MyValue;
  titleUpdatedAt?: string;
  titleTranslatedAt?: string;
};

export type SavingStore = {
  savingState: SavingState;
  savedArticleDetails: DeepReadonly<ArticleTranslationDetails>;
  currentArticleDetails: ArticleTranslationDetails;
  editorReadonly: boolean;
  translatingStatus: TranslatingStatus;
};

// TODO: remove valtio and just use state?
export type SaveFn = UpdateTranslationFn;

const createActions = ({
  state,
  saveFn,
  resetEditor,
  triggerEvent,
}: {
  state: SavingStore;
  saveFn: SaveFn;
  resetEditor: () => void;
  triggerEvent: TriggerEvent;
}) => {
  let savedTimeout: NodeJS.Timeout;

  const actions = {
    async saveIfChanged() {
      if (state.editorReadonly) return;

      // This is triggered when we are back online
      const isOnline = online.isOnline;
      if (!isOnline) return;
      if (state.savingState === 'saving') return;

      const { article, hasChanges } = trackChanges(
        state.savedArticleDetails,
        state.currentArticleDetails,
      );
      if (hasChanges) {
        savedTimeout && clearTimeout(savedTimeout);
        state.savingState = 'saving';
        try {
          await Promise.all([
            // Created inline function, so we can update state.savedArticleDetails as soon as possible
            // instead of waiting for the wait timeout
            (async () => {
              await saveFn(article);
              state.savedArticleDetails = article;
            })(),
            wait(SHOW_SAVING_FOR_MINIMUM),
          ]);

          // Show 'saved' for a bit
          state.savingState = 'saved';
          savedTimeout = setTimeout(() => {
            state.savingState = null;
          }, SHOW_SAVING_FOR_MINIMUM);
        } catch (_error) {
          // Make sure we turn off loading indicator
          state.savingState = 'error';
        }
      }
    },

    debouncedSave: debounce(
      async () => {
        if (state.editorReadonly) return;

        await actions.saveIfChanged();
      },
      DEBOUNCE_SAVE_DELAY,
      true,
    ),

    resetValueToValue(value: ArticleTranslationDetails) {
      if (state.editorReadonly) return;

      actions.debouncedSave.cancel();
      state.currentArticleDetails.body = value.body;
      state.currentArticleDetails.title = value.title;
      state.savedArticleDetails = snapshot(state.currentArticleDetails);
      state.savingState = null;
      savedTimeout && clearTimeout(savedTimeout);
      resetEditor();
    },

    async setTitle(newTitle: string) {
      if (state.translatingStatus.type !== TranslatingType.NONE) {
        state.currentArticleDetails.title = newTitle;
        state.currentArticleDetails.titleTranslatedAt =
          new Date().toISOString();
        state.savedArticleDetails = snapshot(state.currentArticleDetails);
        await saveFn(state.currentArticleDetails);
        return;
      }

      if (state.editorReadonly) return;

      state.currentArticleDetails.title = newTitle;
      await actions.debouncedSave();
    },

    async setBody(newBody: MyValue) {
      if (state.translatingStatus.type !== TranslatingType.NONE) {
        state.currentArticleDetails.body = newBody;
        state.savedArticleDetails = snapshot(state.currentArticleDetails);
        await saveFn(state.currentArticleDetails);
        triggerEvent('article:block-translated', {
          translatingStatus: state.translatingStatus,
        });
        return;
      }

      if (state.editorReadonly) return;

      state.currentArticleDetails.body = newBody;
      await actions.debouncedSave();
    },

    setTranslatingStatus(translatingStatus: TranslatingStatus) {
      state.translatingStatus = translatingStatus;
    },
  };

  return actions;
};

export type DebouncedSave = ReturnType<typeof createActions>['debouncedSave'];

type ArticleSavingStateInput = {
  initialTitle: string;
  initialBody: MyValue;
  initialTittleUpdatedAt?: string;
  initialTitleTranslatedAt?: string;
  editorReadonly: boolean;
  saveFn: SaveFn;
  resetEditor: () => void;
  triggerEvent: TriggerEvent;
};

export type ArticleSavingProxy = {
  savingStore: SavingStore;
  actions: ReturnType<typeof createActions>;
};

export function createArticleSavingProxy({
  initialTitle,
  initialBody,
  initialTittleUpdatedAt,
  initialTitleTranslatedAt,
  editorReadonly,
  saveFn,
  resetEditor,
  triggerEvent,
}: ArticleSavingStateInput): ArticleSavingProxy {
  const savingStore = proxy<SavingStore>({
    savingState: null,
    savedArticleDetails: {
      title: initialTitle,
      body: initialBody,
      titleUpdatedAt: initialTittleUpdatedAt,
      titleTranslatedAt: initialTitleTranslatedAt,
    },
    currentArticleDetails: {
      title: initialTitle,
      body: initialBody,
      titleUpdatedAt: initialTittleUpdatedAt,
      titleTranslatedAt: initialTitleTranslatedAt,
    },
    editorReadonly,
    translatingStatus: { type: TranslatingType.NONE },
  });
  const actions = createActions({
    state: savingStore,
    saveFn,
    resetEditor,
    triggerEvent,
  });

  return {
    savingStore,
    actions,
  };
}
